
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import BrandsStore from "@/mixins/brandsStore";
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import { DataModules } from "@/store/modules/data";
import { IState } from "@/store";
import { IBlueprint, IBlueprintField } from "@/models/provisioning";
import { IProvider } from "@/models/providers";
import {
  IBrandFieldsProviderData,
  IBrandFieldsProviderMethods
} from "@/models/providers/brandFields";
import { CreateElement } from "vue";

@Component({
  computed: {
    ...mapState({
      blueprintsScope(state: IState, getters) {
        return getters[`data/${DataModules.PROVISIONING_BLUEPRINTS}/scope`];
      },
      blueprints(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_BLUEPRINTS,
          scope: this.allBlueprintsScope
        });
      },

      brandFieldsScope(state: IState, getters) {
        return getters[
          `data/${DataModules.PROVISIONING_BRAND}/brandFieldsScope`
        ](this.brandId, this.blueprintId);
      },
      brandFields(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_BRAND,
          scope: this.brandFieldsScope
        });
      },

      brandFieldsValuesScope(state: IState, getters) {
        return getters[
          `data/${DataModules.PROVISIONING_BRAND}/brandFieldsValuesScope`
        ](this.brandId, this.blueprintId);
      },
      brandFieldsValues(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_BRAND,
          scope: this.brandFieldsValuesScope
        });
      }
    })
  }
})
export default class BrandFieldsProvider
  extends Mixins(RequestPromises, BrandsStore, HasValidation)
  implements IProvider {
  // Props
  @Prop({ required: true })
  public blueprintId!: IBlueprint["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Data
  public readonly blueprints!: IBlueprint[];
  public readonly blueprintsScope!: string;
  public readonly brandFieldsScope!: string;
  public readonly brandFields!: IBlueprintField[];
  public readonly brandFieldsValuesScope!: string;
  public readonly brandFieldsValues!: any[];

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;
  public isProcessing = false;

  public guardFunctionalities = [
    "list_brand_provision_fields",
    "list_brand_provision_field_values"
  ];

  // Life-cycle methods
  public async created() {
    if (this.contentAvailable) {
      this.loadData();
    } else {
      this.isLoading = false;
    }
  }

  // Computed

  public get categoryName() {
    return this.$_.get(
      this.$_.find(this.blueprints, ["id", this.blueprintId]),
      "name",
      ""
    );
  }

  public get allBlueprintsScope() {
    return `${this.blueprintsScope}_modal`;
  }

  public get canSave() {
    return this.$userCan("update_brand_provision_field_values");
  }

  public get contentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  @Provide("brandFieldsProvider")
  public providerData(): IBrandFieldsProviderData {
    return {
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      brandFields: this.brandFields,
      brandFieldsValues: this.brandFieldsValues,
      categoryName: this.categoryName,
      canSave: this.canSave,
      isProcessing: this.isProcessing,
      guardFunctionalities: this.guardFunctionalities,
      blueprintId: this.blueprintId
    };
  }

  @Provide("brandFieldsProviderMethods")
  public get providerMethods(): IBrandFieldsProviderMethods {
    return {
      reloadData: this.reloadData,
      getBlueprints: this.getBlueprints,
      getBrandProvisiongFields: this.getBrandProvisiongFields,
      getBrandProvisiongFieldsValues: this.getBrandProvisiongFieldsValues,
      updateBrandFields: this.updateBrandFields,
      setIsProcessing: val => (this.isProcessing = val)
    };
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getBlueprints(this.isReloading),
        this.getBrandProvisiongFields(this.isReloading),
        this.getBrandProvisiongFieldsValues(this.isReloading)
      ]);
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }
  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getBlueprints(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BLUEPRINTS}/list`,
      {
        scope: this.allBlueprintsScope,
        params: {
          with: "blueprint_field_counts",
          order: "name"
        },
        vm: this,
        ignoreStored
      }
    );
  }

  public getBrandProvisiongFields(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BRAND}/listBrandFields`,
      {
        brandId: this.brandId,
        blueprintId: this.blueprintId,
        scope: this.brandFieldsScope,
        vm: this,
        ignoreStored
      }
    );
  }
  public getBrandProvisiongFieldsValues(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BRAND}/listBrandFieldsValues`,
      {
        brandId: this.brandId,
        blueprintId: this.blueprintId,
        scope: this.brandFieldsValuesScope,
        vm: this,
        ignoreStored
      }
    );
  }

  public updateBrandFields(form) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BRAND}/updateBrandFieldsValues`,
      {
        brandId: this.brandId,
        blueprintId: this.blueprintId,
        data: form
      }
    );
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BLUEPRINTS,
      scope: this.allBlueprintsScope,
      vm: this
    });
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BRAND,
      scope: this.brandFieldsScope,
      vm: this
    });
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BRAND,
      scope: this.brandFieldsValuesScope,
      vm: this
    });
  }

  //  Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $brandFieldsData: this.providerData(),
        $brandFieldsMethods: this.providerMethods
      })
    );
  }
}
